import useDebouncedValue from 'hooks/useDebouncedValue';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetDeclarationsParams, GetDeclarationsSortParameter } from 'store/declarations/declaration';
import {
    DeclarationExternalEntity,
    declarationExternalEntityLabelMap,
} from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationStatuses, getDeclarationStatusLabel } from 'store/declarations/enums/common/declaration-status';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';

export type DateRange = { startDate?: string; endDate?: string };

export enum DeclarationParamsKeys {
    QUERY = 'query',
    START_DATE = 'startDate',
    END_DATE = 'endDate',
    SORT = 'sort',
    SORT_DIR = 'sortDirection',
    PAGE = 'page',
    SIZE = 'size',
    DECLARATION_EXTERNAL_ENTITY = 'declarationExternalEntity',
    DECLARATION_STATUS = 'declarationStatus',
    DECLARATION_TYPE = 'declarationType',
    DECLARATION_INTERNAL_TYPE = 'declarationInternalType',
    ARCHIVED = 'archived',
    IS_CORE_TEMPLATE = 'isCoreTemplate',
}

export enum DeclarationFilters {
    DATE_RANGE = 'dateRange',
}

export type FilterTag = {
    key: DeclarationParamsKeys | DeclarationFilters;
    label: string;
    value: string;
    rawValue?: string;
};

const useDeclarationsFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get(DeclarationParamsKeys.QUERY) ?? '';
    const initialStart = searchParams.get(DeclarationParamsKeys.START_DATE) || undefined;
    const initialEnd = searchParams.get(DeclarationParamsKeys.END_DATE) || undefined;
    const initialSort = (searchParams.get(DeclarationParamsKeys.SORT) as GetDeclarationsSortParameter) || undefined;
    const initialSortDir = (searchParams.get(DeclarationParamsKeys.SORT_DIR) as 'ASC' | 'DESC') || undefined;
    const initialPage = Number(searchParams.get(DeclarationParamsKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(DeclarationParamsKeys.SIZE)) || 10;
    const archivedParam = searchParams.get(DeclarationParamsKeys.ARCHIVED);
    const initialArchived = archivedParam == null ? undefined : archivedParam === 'true';
    const isCoreTemplateParam = searchParams.get(DeclarationParamsKeys.IS_CORE_TEMPLATE);
    const initialIsCoreTemplate = isCoreTemplateParam == null ? undefined : isCoreTemplateParam === 'true';
    const initialCountry =
        (searchParams.get(DeclarationParamsKeys.DECLARATION_EXTERNAL_ENTITY) as DeclarationExternalEntity) || undefined;
    const initialStatuses =
        (searchParams.getAll(DeclarationParamsKeys.DECLARATION_STATUS) as DeclarationStatuses[]) ?? [];
    const initialMessageTypes = searchParams.getAll(DeclarationParamsKeys.DECLARATION_TYPE) as MessageTypes[];
    const initialDeclarationTypes = searchParams.getAll(
        DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE
    ) as DeclarationInternalType[];

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [dates, setDates] = useState<DateRange>({ startDate: initialStart, endDate: initialEnd });
    const [sortState, setSortState] = useState<{
        sort: GetDeclarationsSortParameter | undefined;
        sortDirection: 'ASC' | 'DESC' | undefined;
    }>({ sort: initialSort, sortDirection: initialSortDir });
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);
    const [country, setCountry] = useState<DeclarationExternalEntity | undefined>(initialCountry);
    const [statuses, setStatuses] = useState<DeclarationStatuses[]>(initialStatuses);
    const [messageTypes, setMessageTypes] = useState<MessageTypes[]>(initialMessageTypes);
    const [declarationTypes, setDeclarationTypes] = useState<DeclarationInternalType[]>(initialDeclarationTypes);
    const [archived, setArchived] = useState<boolean | undefined>(initialArchived);
    const [isCoreTemplate, setIsCoreTemplate] = useState<boolean | undefined>(initialIsCoreTemplate);

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: Partial<GetDeclarationsParams>) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            const applyArray = (key: string, values?: (string | number)[] | null) => {
                searchParam.delete(key);
                if (values && values.length) {
                    values?.forEach((v) => searchParam.append(key, String(v)));
                }
            };

            apply(DeclarationParamsKeys.QUERY, next.query);
            apply(DeclarationParamsKeys.START_DATE, next.startDate);
            apply(DeclarationParamsKeys.END_DATE, next.endDate);
            apply(DeclarationParamsKeys.SORT, next.sort);
            apply(DeclarationParamsKeys.SORT_DIR, next.sortDirection);
            apply(DeclarationParamsKeys.PAGE, next.page?.toString());
            apply(DeclarationParamsKeys.SIZE, next.size?.toString());
            apply(DeclarationParamsKeys.DECLARATION_EXTERNAL_ENTITY, next.declarationExternalEntity);
            apply(DeclarationParamsKeys.ARCHIVED, next.archived === undefined ? undefined : String(next.archived));
            apply(
                DeclarationParamsKeys.IS_CORE_TEMPLATE,
                next.isCoreTemplate === undefined ? undefined : String(next.isCoreTemplate)
            );

            applyArray(
                DeclarationParamsKeys.DECLARATION_STATUS,
                next.declarationStatus as DeclarationStatuses[] | undefined
            );
            applyArray(DeclarationParamsKeys.DECLARATION_TYPE, next.declarationType as MessageTypes[] | undefined);
            applyArray(
                DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE,
                next.declarationInternalType as DeclarationInternalType[] | undefined
            );

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            query: debouncedQuery,
            startDate: dates.startDate,
            endDate: dates.endDate,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            declarationExternalEntity: country,
            archived,
            isCoreTemplate,
            declarationStatus: statuses?.length ? (statuses as DeclarationStatuses[]) : undefined,
            declarationType: messageTypes?.length ? (messageTypes as MessageTypes[]) : undefined,
            declarationInternalType: declarationTypes?.length
                ? (declarationTypes as DeclarationInternalType[])
                : undefined,
        });
    }, [
        debouncedQuery,
        dates.startDate,
        dates.endDate,
        writeParams,
        sortState.sort,
        sortState.sortDirection,
        page,
        size,
        country,
        statuses,
        messageTypes,
        declarationTypes,
        archived,
        isCoreTemplate,
    ]);

    const appliedParams = useMemo(
        () => ({
            query: debouncedQuery || undefined,
            startDate: dates.startDate,
            endDate: dates.endDate,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            declarationExternalEntity: country,
            declarationStatus: statuses,
            declarationType: messageTypes,
            declarationInternalType: declarationTypes,
            archived,
            isCoreTemplate,
        }),
        [
            debouncedQuery,
            dates.startDate,
            dates.endDate,
            sortState.sort,
            sortState.sortDirection,
            page,
            size,
            country,
            statuses,
            messageTypes,
            declarationTypes,
            archived,
            isCoreTemplate,
        ]
    );

    const tags: FilterTag[] = useMemo(() => {
        const list: FilterTag[] = [];

        const { startDate, endDate } = dates;
        if (startDate && endDate) {
            list.push({
                key: DeclarationFilters.DATE_RANGE,
                label: 'Date',
                value: `${moment(startDate).format('YYYY-MM-DD')} - ${moment(endDate).format('YYYY-MM-DD')}`,
            });
        }

        if (country) {
            list.push({
                key: DeclarationParamsKeys.DECLARATION_EXTERNAL_ENTITY,
                label: 'Country',
                value: declarationExternalEntityLabelMap[country] ?? country,
            });
        }

        if (statuses.length) {
            for (let i = 0; i < statuses.length; i++) {
                list.push({
                    key: DeclarationParamsKeys.DECLARATION_STATUS,
                    label: 'Declaration Status',
                    value: getDeclarationStatusLabel(statuses[i] as DeclarationStatuses),
                    rawValue: statuses[i],
                });
            }
        }

        if (messageTypes.length) {
            for (let i = 0; i < messageTypes.length; i++) {
                list.push({
                    key: DeclarationParamsKeys.DECLARATION_TYPE,
                    label: 'Form Type',
                    value: messageTypes[i],
                });
            }
        }

        if (declarationTypes.length) {
            for (let i = 0; i < declarationTypes.length; i++) {
                list.push({
                    key: DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE,
                    label: 'Declaration Type',
                    value: declarationTypes[i],
                });
            }
        }

        return list;
    }, [dates, country, statuses, messageTypes, declarationTypes]);

    const clearFilter = useCallback((key: string, value?: string) => {
        switch (key) {
            case DeclarationParamsKeys.QUERY: {
                setQueryInputVal('');
                break;
            }

            case DeclarationFilters.DATE_RANGE: {
                setDates({
                    startDate: undefined,
                    endDate: undefined,
                });
                break;
            }

            case DeclarationParamsKeys.DECLARATION_EXTERNAL_ENTITY: {
                setCountry(undefined);
                break;
            }

            case DeclarationParamsKeys.DECLARATION_STATUS: {
                if (value) {
                    setStatuses((prev) => prev.filter((s) => s !== value));
                } else {
                    setStatuses([]);
                }
                break;
            }

            case DeclarationParamsKeys.DECLARATION_TYPE: {
                if (value) {
                    setMessageTypes((prev) => prev.filter((s) => s !== value));
                } else {
                    setMessageTypes([]);
                }
                break;
            }

            case DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE: {
                if (value) {
                    setDeclarationTypes((prev) => prev.filter((s) => s !== value));
                } else {
                    setDeclarationTypes([]);
                }
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setDates({
            startDate: undefined,
            endDate: undefined,
        });

        setPage(0);
        setCountry(undefined);
        setQueryInputVal('');
        setStatuses([]);
        setMessageTypes([]);
        setDeclarationTypes([]);
        setIsCoreTemplate(undefined);
        setArchived(undefined);
    }, []);

    return {
        queryInputVal,
        archived,
        tags,
        dates,
        appliedParams,
        debouncedQuery,
        sortState,
        setQueryInputVal,
        setDates,
        setSortState,
        clearFilter,
        clearAll,
        setPage,
        setStatuses,
        setCountry,
        setSize,
        setMessageTypes,
        setDeclarationTypes,
        setArchived,
        setIsCoreTemplate,
    };
};

export default useDeclarationsFilters;
